import { useEffect, useMemo, useState } from 'react';
import { Axis3d, ChevronDown, UploadCloud } from 'lucide-react';
import { listImuFirmwares } from '../../api/ImuOtaApi';
import { hasFullAccess } from '../../utils/auth';
import styles from '../../styles/components/ImuDiagnosticsPanel.module.scss';

const formatCelsius = value =>
  typeof value === 'number' ? `${value.toFixed(1)} °C` : String(value);

const formatBoolean = value => (value === true ? 'Yes' : value === false ? 'No' : String(value));

const formatErrorRegister = value => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return String(value);
  }
  if (numeric === 0) {
    return '0 (no errors)';
  }
  return `0x${numeric.toString(16).toUpperCase().padStart(2, '0')} (${numeric})`;
};

const IMU_VENDOR_NAMES = {
  0x000000d0: 'MOBA',
  0x0000054b: 'ZYAX',
  0x00005351: 'SIGNAL_QUEST',
};

const formatVendorId = value => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return String(value);
  }
  const hex = `0x${numeric.toString(16).toUpperCase().padStart(8, '0')}`;
  return `${hex} (${IMU_VENDOR_NAMES[numeric] || 'Unknown'})`;
};

// Known diagnostic fields rendered first, in this order. Unknown fields
// reported by newer firmware are appended generically after these.
const KNOWN_FIELDS = [
  { key: 'serial_number', label: 'Serial number' },
  { key: 'vendor_id', label: 'Vendor', format: formatVendorId },
  { key: 'firmware_version', label: 'Firmware' },
  { key: 'imu_temperature_c', label: 'IMU temperature', format: formatCelsius },
  { key: 'target_temperature_c', label: 'Target temperature', format: formatCelsius },
  {
    key: 'temperature_converged',
    label: 'Temperature converged',
    format: formatBoolean,
    tone: value => (value === false ? 'warning' : 'success'),
  },
  { key: 'mcu_temperature_c', label: 'MCU temperature', format: formatCelsius },
  {
    key: 'error_register',
    label: 'Error register',
    format: formatErrorRegister,
    tone: value => (Number(value) !== 0 ? 'error' : 'success'),
  },
];

const genericLabel = key =>
  key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const formatGenericValue = value => {
  if (typeof value === 'boolean') {
    return formatBoolean(value);
  }
  if (value === null || value === undefined) {
    return '-';
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return '[Object]';
    }
  }
  return String(value);
};

const getImuHealth = imu => {
  if (Number(imu?.error_register) !== 0) {
    return 'error';
  }
  if (imu?.temperature_converged === false) {
    return 'warning';
  }
  return 'success';
};

const ImuDiagnosticsPanel = ({
  imus,
  imuInstallationsByCan = {},
  isDeviceOnline = true,
  onSendCommand,
}) => {
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [upgradeTarget, setUpgradeTarget] = useState(null); // { id, currentVersion }
  const [upgradeVersion, setUpgradeVersion] = useState('');
  const [firmwareVersions, setFirmwareVersions] = useState(null); // null while loading

  // Load available firmware versions each time the upgrade dialog opens,
  // and close the dialog on Escape.
  useEffect(() => {
    if (!upgradeTarget) {
      return undefined;
    }
    let cancelled = false;
    setFirmwareVersions(null);
    listImuFirmwares()
      .then(response => {
        if (!cancelled) {
          setFirmwareVersions((response.data || []).map(item => item.version));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFirmwareVersions([]);
        }
      });
    const handleEscape = event => {
      if (event.key === 'Escape') {
        setUpgradeTarget(null);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      cancelled = true;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [upgradeTarget]);

  const installationById = useMemo(() => {
    const map = {};
    Object.entries(imuInstallationsByCan || {}).forEach(([canPort, installations]) => {
      (installations || []).forEach(installation => {
        map[String(installation.imu_id)] = { ...installation, canPort };
      });
    });
    return map;
  }, [imuInstallationsByCan]);

  const imuIds = useMemo(
    () => Object.keys(imus || {}).sort((a, b) => Number(a) - Number(b)),
    [imus]
  );

  if (imuIds.length === 0) {
    return null;
  }

  const issueCount = imuIds.filter(id => getImuHealth(imus[id]) !== 'success').length;
  const allExpanded = expandedIds.size === imuIds.length;

  const toggleImu = id => {
    setExpandedIds(previous => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    setExpandedIds(allExpanded ? new Set() : new Set(imuIds));
  };

  const openUpgradeDialog = (id, currentVersion) => {
    setUpgradeVersion('');
    setUpgradeTarget({ id, currentVersion });
  };

  const confirmUpgrade = () => {
    if (!upgradeTarget || !upgradeVersion) {
      return;
    }
    const command = `imu_fw_${upgradeTarget.id}_${upgradeVersion}`;
    setUpgradeTarget(null);
    onSendCommand?.(command);
  };

  // Firmware upgrades are an OTA feature, hidden from the partner role
  const canUpgradeFirmware = Boolean(onSendCommand) && hasFullAccess();

  const renderDetailTiles = (imu, id) => {
    const knownKeys = KNOWN_FIELDS.map(field => field.key);
    const extraKeys = Object.keys(imu || {}).filter(key => !knownKeys.includes(key));

    // Anchor the upgrade box to firmware version when reported, otherwise to
    // the serial number, so an IMU that reports either one can be upgraded.
    const has = key => Object.prototype.hasOwnProperty.call(imu || {}, key);
    const upgradeAnchorKey = has('firmware_version')
      ? 'firmware_version'
      : has('serial_number')
        ? 'serial_number'
        : null;

    const tiles = KNOWN_FIELDS.filter(field =>
      Object.prototype.hasOwnProperty.call(imu || {}, field.key)
    )
      .map(field => ({
        key: field.key,
        label: field.label,
        value: field.format ? field.format(imu[field.key]) : String(imu[field.key]),
        tone: field.tone ? field.tone(imu[field.key]) : null,
      }))
      .concat(
        extraKeys.map(key => ({
          key,
          label: genericLabel(key),
          value: formatGenericValue(imu[key]),
          tone: null,
        }))
      );

    return (
      <dl className={styles.detailGrid}>
        {tiles.map(tile => {
          const withUpgrade =
            canUpgradeFirmware && upgradeAnchorKey !== null && tile.key === upgradeAnchorKey;
          return (
            <div
              key={tile.key}
              className={`${tile.tone ? styles[tile.tone] : ''} ${
                withUpgrade ? styles.firmwareTile : ''
              }`}
            >
              <dt>{tile.label}</dt>
              <dd>{tile.value}</dd>
              {withUpgrade && (
                <button
                  type="button"
                  className={styles.upgradeButton}
                  onClick={() => openUpgradeDialog(id, imu.firmware_version)}
                  disabled={!isDeviceOnline}
                  title={isDeviceOnline ? 'Send a firmware upgrade command' : 'Hub is offline'}
                >
                  <UploadCloud size={12} /> Upgrade
                </button>
              )}
            </div>
          );
        })}
      </dl>
    );
  };

  return (
    <section className={styles.panel}>
      <div className={styles.panelHeader}>
        <div>
          <Axis3d size={17} />
          <span>
            <strong>IMU diagnostics</strong>
            <small>Per-sensor health from the latest status snapshot</small>
          </span>
        </div>
        <div className={styles.headerActions}>
          <span
            className={`${styles.summaryBadge} ${issueCount > 0 ? styles.summaryIssues : styles.summaryOk}`}
          >
            <i />
            {imuIds.length} IMU{imuIds.length > 1 ? 's' : ''}
            {issueCount > 0 ? ` · ${issueCount} with issues` : ' · all healthy'}
          </span>
          <button type="button" className={styles.expandAllButton} onClick={toggleAll}>
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </button>
        </div>
      </div>

      <div className={`${styles.list} ${!isDeviceOnline ? styles.offlineData : ''}`}>
        {imuIds.map(id => {
          const imu = imus[id] || {};
          const installation = installationById[id];
          const name = installation?.place || `IMU ${id}`;
          const health = getImuHealth(imu);
          const isExpanded = expandedIds.has(id);
          const metaParts = [`ID ${id}`];
          if (installation) {
            metaParts.push(`CAN ${installation.canPort}`);
          }
          if (imu.serial_number !== undefined) {
            metaParts.push(`SN ${imu.serial_number}`);
          }

          return (
            <article key={id} className={styles.row}>
              <button
                type="button"
                className={styles.rowHeader}
                onClick={() => toggleImu(id)}
                aria-expanded={isExpanded}
              >
                <span className={`${styles.healthDot} ${styles[health]}`} />
                <span className={styles.rowTitle}>
                  <strong>{name}</strong>
                  <small>{metaParts.join(' · ')}</small>
                </span>
                <span className={styles.rowBadges}>
                  {typeof imu.imu_temperature_c === 'number' && (
                    <span
                      className={`${styles.badge} ${
                        imu.temperature_converged === false ? styles.badgeWarning : styles.badgeOk
                      }`}
                    >
                      {formatCelsius(imu.imu_temperature_c)}
                    </span>
                  )}
                  {Number(imu.error_register) !== 0 && (
                    <span className={`${styles.badge} ${styles.badgeError}`}>
                      ERR {formatErrorRegister(imu.error_register)}
                    </span>
                  )}
                </span>
                <ChevronDown
                  size={16}
                  className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`}
                />
              </button>
              {isExpanded && renderDetailTiles(imu, id)}
            </article>
          );
        })}
      </div>

      {upgradeTarget && (
        <>
          <button
            type="button"
            aria-label="Close upgrade dialog"
            className={styles.modalBackdrop}
            onClick={() => setUpgradeTarget(null)}
          />
          <div
            className={styles.upgradeModal}
            role="dialog"
            aria-modal="true"
            aria-label={`Upgrade firmware of IMU ${upgradeTarget.id}`}
          >
            <header>
              <strong>Upgrade IMU {upgradeTarget.id} firmware</strong>
              <small>
                Current version:{' '}
                {upgradeTarget.currentVersion !== undefined
                  ? String(upgradeTarget.currentVersion)
                  : 'unknown'}
              </small>
            </header>
            <label>
              <span>Target version</span>
              <select
                value={upgradeVersion}
                onChange={event => setUpgradeVersion(event.target.value)}
                autoFocus
              >
                <option value="">
                  {firmwareVersions === null ? 'Loading versions…' : 'Select a version'}
                </option>
                {(firmwareVersions || []).map(version => (
                  <option key={version} value={version}>
                    {version}
                  </option>
                ))}
              </select>
            </label>
            {firmwareVersions?.length === 0 && (
              <small className={styles.modalHint}>
                No firmware packages uploaded yet — add one under Deployments → IMU firmware.
              </small>
            )}
            {upgradeVersion && (
              <small className={styles.modalHint}>
                Command <code>{`imu_fw_${upgradeTarget.id}_${upgradeVersion}`}</code> will be sent
                to the hub.
              </small>
            )}
            <footer>
              <button
                type="button"
                className={styles.modalSecondary}
                onClick={() => setUpgradeTarget(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.modalPrimary}
                onClick={confirmUpgrade}
                disabled={!upgradeVersion}
              >
                Send upgrade
              </button>
            </footer>
          </div>
        </>
      )}
    </section>
  );
};

export default ImuDiagnosticsPanel;
